import React, { useEffect, useState } from 'react';

import { useFreecodeContextRun, useFreecodeContextLayout, useFreecodeContextData } from 'hooks/useContext';
import { buttonIds, editorTypes } from 'components/FreecodePage/FreecodeLayout/FreecodeLayoutConstants';
import { OptionContainer } from 'components/FreecodePage/FreecodeTabSelector/styles';
import { FreecodeButton } from 'components/Button/RectangleButton';
import Dropdown from 'components/Dropdown/Dropdown';
import { useQueryParams } from 'hooks/useQueryParams';
import { getConfig } from 'functions/getConfig';
import useFreecodeLivePrimus from '../useFreecodeLivePrimus';

const config = getConfig();

export const LanguageDropdown = () => {
  const { courseId } = useQueryParams();

  const { ticket, test, testImpl, languageData, input } = useFreecodeContextData();
  const { language, setLanguage, setCodeSol, levelToRun, codeTestSolutions } = useFreecodeContextRun();
  const { editorType } = useFreecodeContextLayout();

  const sendTicketPrimusRoomRequest = useFreecodeLivePrimus();

  const [options, setOptions] = useState([]);

  useEffect(() => {
    let languageKeys = Object.keys(languageData);
    if (languageKeys.length <= 1) return setOptions([]);
    setOptions(
      languageKeys.map((key) => {
        return { value: key, label: languageData[key].CurrentVersion, icon: languageData[key].icon };
      })
    );
  }, [languageData]);

  const doLanguageSelection = (value) => {
    const desiredLanguage = value.value;
    if (!desiredLanguage) return;
    if (language === desiredLanguage) return;
    if (!languageData[desiredLanguage]) return;
    let code =
      codeTestSolutions[desiredLanguage] ||
      localStorage.getItem(`fsEditor-${test.category}-${test._id}-${desiredLanguage}`) ||
      input?.levelData?.[String(levelToRun)]?.[desiredLanguage]?.latestRun?.code ||
      testImpl.generated?.skeletons?.[desiredLanguage] ||
      'Code not found...';
    setCodeSol(code);
    localStorage.setItem(`fsEditor-${test.category}-${test._id}-language`, desiredLanguage);
    if (courseId) {
      document.cookie = `fsCourse-${courseId}-language=${desiredLanguage}; Domain=${config.COOKIE}; Max-Age=604800; Path=/; SameSite=Lax`;
    }
    setLanguage(desiredLanguage);
    if (ticket?._id)
      sendTicketPrimusRoomRequest({
        operation: 'ticketCodeData',
        solution: { code, language: desiredLanguage },
      });
  };

  return (
    <Dropdown
      isSmallDropdown
      id={editorType === editorTypes.liveCoding ? buttonIds.liveCodingSelectedLanguage : buttonIds.selectLanguage}
      options={options}
      icon={languageData[language].icon}
      value={languageData[language].CurrentVersion}
      onChange={doLanguageSelection}
      disabled={editorType === editorTypes.liveCoding}
    />
  );
};

// Language dropdown for skeleton editor in FreecodeCreator
export const SimpleLanguageDropdown = () => {
  const { languageData } = useFreecodeContextData();
  const { language, setLanguage } = useFreecodeContextRun();
  const { editorType } = useFreecodeContextLayout();

  const [options, setOptions] = useState([]);

  useEffect(() => {
    let languageKeys = Object.keys(languageData);
    if (languageKeys.length <= 1) return setOptions([]);
    setOptions(
      languageKeys.map((key) => {
        return { value: key, label: languageData[key].CurrentVersion, icon: languageData[key].icon };
      })
    );
  }, [languageData]);

  const doLanguageSelection = (value) => {
    const desiredLanguage = value.value;
    if (!desiredLanguage) return;
    if (language === desiredLanguage) return;
    if (!languageData[desiredLanguage]) return;
    setLanguage(desiredLanguage);
  };

  return (
    <Dropdown
      id={buttonIds.selectLanguage}
      isFreecode
      isSmallDropdown
      options={options}
      icon={languageData[language].icon}
      value={languageData[language].CurrentVersion}
      onChange={doLanguageSelection}
      disabled={editorType === editorTypes.liveCoding}
      leftDrop
    />
  );
};

const ExerciseResetDropdown = ({ language, levelToRun, setCodeSol, testImpl, input }) => {
  let resetOptions = [{ label: 'Initial state', value: 'skeleton' }];

  const levelInputForLang = input?.levelData?.[String(levelToRun)]?.[language];
  if (levelInputForLang) {
    if (levelInputForLang.latestRun) resetOptions.push({ label: 'Last run', value: 'latestRun' });
    if (levelInputForLang.highScore) resetOptions.push({ label: 'High score', value: 'highScore' });
    if (levelInputForLang.firstSuccess) resetOptions.push({ label: 'First success', value: 'firstSuccess' });
  }

  const resetCode = (value) => {
    let newCode = 'Code not found...';
    if (value?.value === 'skeleton') newCode = testImpl.generated.skeletons[language];
    else if (levelInputForLang[value?.value]?.code) newCode = levelInputForLang[value?.value].code;
    setCodeSol(newCode);
    localStorage.setItem(`fsEditor-exercise-${testImpl.test}-${language}`, newCode);
  };

  return <Dropdown isSmallDropdown options={resetOptions} value="Reset Code" onChange={resetCode} />;
};

export const LanguageSelection = () => {
  const { editorType } = useFreecodeContextLayout();
  const { ticket, test, testImpl, input, setShowFlowOverlay } = useFreecodeContextData();
  const { language, levelToRun, setCodeSol } = useFreecodeContextRun();
  const sendTicketPrimusRoomRequest = useFreecodeLivePrimus();

  const resetCode = () => {
    let newCode = testImpl.generated?.skeletons?.[language] ?? 'Code not found...';
    setCodeSol(newCode);
    if (editorType !== editorTypes.freecode && editorType !== editorTypes.freecodeInspect)
      localStorage.setItem(`fsEditor-${test.category}-${test._id}-${language}`, newCode);
    if (ticket?._id)
      sendTicketPrimusRoomRequest({
        operation: 'ticketCodeData',
        solution: { code: newCode, language },
      });
  };

  return (
    editorType !== editorTypes.creator && (
      <OptionContainer>
        {editorType === editorTypes.creatorInspect && testImpl.isFlow && (
          <FreecodeButton small onClick={() => setShowFlowOverlay(true)}>
            Review Flow
          </FreecodeButton>
        )}
        {editorType !== editorTypes.creatorInspect && LanguageDropdown()}
        {editorType === editorTypes.exercise && (
          <ExerciseResetDropdown
            testImpl={testImpl}
            setCodeSol={setCodeSol}
            levelToRun={levelToRun}
            language={language}
            input={input}
          />
        )}
        {editorType !== editorTypes.exercise && editorType !== editorTypes.liveCoding && (
          <FreecodeButton small onClick={resetCode}>
            Reset Code
          </FreecodeButton>
        )}
      </OptionContainer>
    )
  );
};
